import type { LucideIcon } from 'lucide-react'
import {
  Archive,
  Inbox,
  Mail,
  MessageSquare,
  Mic,
  Moon,
  Puzzle,
  Sparkles,
  Workflow,
} from 'lucide-react'
import type { KindFilter } from './types'

/** Named inbox folders for mode-panel navigation (list-surface continuity). */
export type InboxFolderId =
  | 'inbox'
  | 'unread'
  | 'flows'
  | 'chats'
  | 'suggestions'
  | 'voice'
  | 'email'
  | 'extensions'
  | 'snoozed'
  | 'archived'

/** Unread is list focus on ExtensionFilterBar - not a sidebar folder. */
export type InboxListFocus = 'all' | 'unread'

export type InboxSortDir = 'newest' | 'oldest'

export type InboxFolderMeta = {
  id: InboxFolderId
  label: string
  icon: LucideIcon
  /** Hide folder row when count is zero (extensions only). */
  hideIfEmpty?: boolean
}

/**
 * Mode-panel nouns only. Unread is list focus on the filter row
 * (see InboxListFocus); keep `unread` in InboxFolderId for legacy events.
 */
export const INBOX_FOLDERS: InboxFolderMeta[] = [
  { id: 'inbox', label: 'Inbox', icon: Inbox },
  { id: 'flows', label: 'Flows', icon: Workflow },
  { id: 'chats', label: 'Chats', icon: MessageSquare },
  { id: 'suggestions', label: 'Suggestions', icon: Sparkles },
  { id: 'voice', label: 'Voice', icon: Mic },
  { id: 'email', label: 'Email', icon: Mail },
  { id: 'extensions', label: 'Extensions', icon: Puzzle, hideIfEmpty: true },
  { id: 'snoozed', label: 'Snoozed', icon: Moon },
  { id: 'archived', label: 'Archived', icon: Archive },
]

const FOLDER_LABELS: Record<InboxFolderId, string> = {
  inbox: 'Inbox',
  unread: 'Unread',
  flows: 'Flows',
  chats: 'Chats',
  suggestions: 'Suggestions',
  voice: 'Voice',
  email: 'Email',
  extensions: 'Extensions',
  snoozed: 'Snoozed',
  archived: 'Archived',
}

export function inboxFolderLabel(id: InboxFolderId): string {
  return FOLDER_LABELS[id] ?? id
}

/**
 * Canonicalize a folder id from events / deep links.
 * Legacy `unread` -> Inbox noun + Unread list focus.
 */
export function canonicalizeInboxFolder(folderId: InboxFolderId): {
  folderId: InboxFolderId
  listFocus: InboxListFocus
} {
  if (folderId === 'unread') {
    return { folderId: 'inbox', listFocus: 'unread' }
  }
  return { folderId, listFocus: 'all' }
}

/**
 * Inline AddressBar trail for the Inbox index.
 * Root = Inbox only; noun folder = Inbox / Email. Never Inbox / Inbox.
 */
export function inboxIndexCrumbs(
  folderId: InboxFolderId,
  opts?: { onInboxClick?: () => void },
): Array<{ label: string; path: null; onClick?: () => void }> {
  const nounId = folderId === 'unread' ? 'inbox' : folderId
  if (nounId === 'inbox') {
    return [{ label: 'Inbox', path: null }]
  }
  return [
    {
      label: 'Inbox',
      path: null,
      onClick: opts?.onInboxClick,
    },
    { label: inboxFolderLabel(nounId), path: null },
  ]
}

/** Map folder id to API `kind` when 1:1. Undefined = no kind constraint. */
export function folderToApiKind(folderId: InboxFolderId): string | undefined {
  switch (folderId) {
    case 'flows':
      return 'flow_output'
    case 'voice':
      return 'voice'
    case 'email':
      return 'email'
    case 'extensions':
      return 'extension_event'
    case 'chats':
      return 'agent_chat'
    default:
      return undefined
  }
}

/** Map folder id to API `status` when the folder is status-scoped. */
export function folderToApiStatus(folderId: InboxFolderId): string | undefined {
  switch (folderId) {
    case 'archived':
      return 'archived'
    case 'snoozed':
      return 'snoozed'
    default:
      return undefined
  }
}

/** Map legacy KindTabs filter to folder id (Browse shortcuts, inbox-filter-change). */
export function kindFilterToFolder(filter: KindFilter): InboxFolderId {
  switch (filter) {
    case 'all':
      return 'inbox'
    case 'voice':
      return 'voice'
    case 'email':
      return 'email'
    case 'agent':
      return 'inbox'
    case 'suggestion':
    case 'invite':
      return 'suggestions'
    case 'chat':
      return 'chats'
    case 'flow':
      return 'flows'
    case 'extension':
      return 'extensions'
    case 'snoozed':
      return 'snoozed'
    case 'archived':
      return 'archived'
    default:
      return 'inbox'
  }
}

/** Map folder id to legacy KindFilter for InboxFeed reuse paths. */
export function folderToKindFilter(folderId: InboxFolderId): KindFilter {
  switch (folderId) {
    case 'inbox':
    case 'unread':
      return 'all'
    case 'flows':
      return 'flow'
    case 'chats':
      return 'chat'
    case 'suggestions':
      return 'suggestion'
    case 'voice':
      return 'voice'
    case 'email':
      return 'email'
    case 'extensions':
      return 'extension'
    case 'snoozed':
      return 'snoozed'
    case 'archived':
      return 'archived'
    default:
      return 'all'
  }
}

export const INBOX_FOLDER_CHANGE_EVENT = 'inbox-folder-change'

export function dispatchInboxFolderChange(
  folderId: InboxFolderId,
  activeExtension: string | null = null,
): void {
  window.dispatchEvent(
    new CustomEvent(INBOX_FOLDER_CHANGE_EVENT, {
      detail: { folderId, activeExtension },
    }),
  )
}

export const OPEN_INBOX_MAIN_EVENT = 'open-inbox-main'

export function dispatchOpenInboxMain(): void {
  window.dispatchEvent(new CustomEvent(OPEN_INBOX_MAIN_EVENT))
}
